import cancelIcon from '@SvgIcon/close.svg'
import checkIcon from '@SvgIcon/checkmark.svg'

import autocomplete from 'autocompleter'
import Mailcheck from 'mailcheck'
import svgIconHtml from '../../svgIconHtml'
import emitter from '../../mixin/emitter'
import he from 'he'
import { MaskInput } from 'maska'

export default {
  name: 'UiFormInput',

  mixins: [emitter],

  inheritAttrs: false,

  inject: {
    uiFormItem: {
      default: '',
    },
  },

  // language=HTML
  template: `
    <div
      :class="[
        type === 'textarea' ? 'ui-textarea' : 'ui-input',
        {
          'is-inline': inline,
          'is-disabled': disabled,
          'ui-input-group': $slots.prepend || prepend || $slots.append || append,
          'ui-input-group--append': $slots.append || append,
          'ui-input-group--prepend': $slots.prepend || prepend,
        }
      ]"
    >
      <template v-if="type !== 'textarea'">
        <div class="ui-input-group__prepend" v-if="$slots.prepend || prepend">
          <slot name="prepend">{{ prepend }}</slot>
        </div>
        <input
          ref="input"
          class="ui-input__inner"
          :class="inputClass"
          :style="computedInputStyle"
          v-bind="$attrs"
          :type="type"
          :id="computedId"
          :disabled="disabled"
          :value="currentValue"
          @input="handleInput"
          @change="handleChange"
          @blur="handleBlur"
        />
        <div
          v-if="mailCheckedEmail && !hideMailCheckedEmail"
          style="font-size: 14px"
        >
          Did you mean <span>{{ mailCheckedEmail }}</span>?
          <button
            type="button"
            class="tiny alert button"
            style="margin-right: 4px"
            @click="acceptMailCheckedEmail"
          >
            ${svgIconHtml(checkIcon)}
          </button>
          <button
            type="button"
            class="tiny button"
            @click="rejectMailCheckedEmail"
          >
            ${svgIconHtml(cancelIcon)}
          </button>
        </div>
        <div class="ui-input-group__append" v-if="$slots.append || append">
          <slot name="append">{{ append }}</slot>
        </div>
      </template>

      <textarea
        v-else
        ref="input"
        class="ui-textarea__inner"
        :class="inputClass"
        :style="computedInputStyle"
        v-bind="$attrs"
        :id="computedId"
        :disabled="disabled"
        :value="currentValue"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"></textarea>
    </div>
  `,

  props: {
    type: {
      type: String,
      default: 'text',
    },

    id: String,
    disabled: Boolean,
    value: [String, Number],
    inline: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: [Array, Object, String],
      default() {
        return []
      },
    },
    inputStyle: {
      type: [Array, Object],
      default() {
        return {}
      },
    },
    maxChars: {
      type: Number,
    },
    maxWords: {
      type: Number,
    },
    mask: {
      type: String,
    },
    maskOpts: {
      type: Object,
      default() {
        return {}
      },
    },
    // [{ value: 'opt1', label: 'Option 1' }]
    selectAutocomplete: {
      type: Array,
      default() {
        return []
      },
    },
    prepend: String,
    append: String,
    autogrow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentValue: null,
      mailCheckedEmail: null,
      hideMailCheckedEmail: false,
    }
  },

  computed: {
    computedId() {
      if (this.id) {
        return this.id
      }

      return this.uiFormItem.computedId
    },

    selectAutocompleteValues() {
      if (!Array.isArray(this.selectAutocomplete)) {
        return []
      }

      return this.selectAutocomplete.map((value) => {
        if (typeof value === 'string') {
          const unescapedValue = he.decode(value)
          return { label: unescapedValue, value: unescapedValue }
        }
        return value
      })
    },

    numChars() {
      if (this.type !== 'file' && this.currentValue) {
        return String(this.currentValue).length
      }
      return 0
    },

    numWords() {
      if (this.type !== 'file' && this.currentValue) {
        return String(this.currentValue).trim().split(/\s+/).length
      }
      return 0
    },

    computedInputStyle() {
      const style = { ...this.inputStyle }
      if (this.autogrow) {
        style['field-sizing'] = 'content'
        style.height = 'auto'
      }
      return style
    },
  },

  watch: {
    /**
     * Responsible for updating internal value when outside value changes.
     */
    value() {
      this.refreshCurrentValue()
    },
  },

  methods: {
    refreshCurrentValue() {
      this.currentValue = this.value === undefined || this.value === null ? '' : this.value
    },

    handleInput(ev) {
      if (this.type === 'file') {
        this.$emit('input', ev.target.files)
      } else {
        const { value } = ev.target
        this.changeValue(value)
      }
    },

    handleChange(ev) {
      if (this.type === 'file') {
        this.$emit('change', ev.target.files)
      } else {
        this.$emit('change', ev.target.value)
      }
    },

    handleBlur(ev) {
      this.verifyEmail()
      // Force input to reflect potentially truncated value from handleInput:
      if (this.type !== 'file' && ev.target.value !== this.currentValue) {
        ev.target.value = this.currentValue
      }
      this.$emit('blur', ev)
      this.dispatch('UiFormItem', 'ui.form.blur', [this.currentValue])
    },

    changeValue(value) {
      this.setCurrentValue(value)
      this.$emit('input', value)
    },

    setCurrentValue(value) {
      this.currentValue = value
    },

    verifyEmail() {
      if (this.type !== 'email') {
        return
      }

      this.hideMailCheckedEmail = false

      Mailcheck.run({
        email: this.currentValue,
        suggested: (suggestion) => {
          this.mailCheckedEmail = suggestion.full
        },
        empty() {
          // nothing wrong with the email
        },
      })
    },

    acceptMailCheckedEmail() {
      this.currentValue = this.mailCheckedEmail
      this.$emit('input', this.mailCheckedEmail)
      this.mailCheckedEmail = null
    },

    rejectMailCheckedEmail() {
      this.mailCheckedEmail = null
    },

    getTruncatedValue(value) {
      if (this.maxWords) {
        const words = value.split(/\s+/).filter(word => word.length > 0)
        const wordCount = words.length
        if (wordCount > this.maxWords) {
          value = words.slice(0, this.maxWords).join(' ')
        }
      }
      return value
    },
  },

  created() {
    this.refreshCurrentValue()
  },

  mounted() {
    this.$nextTick(() => {
      if (this.mask) {
        new MaskInput(this.$refs.input, { mask: this.mask, ...this.maskOpts })
      }

      if (this.selectAutocompleteValues.length > 0) {
        autocomplete({
          input: this.$refs.input,
          showOnFocus: true,
          disableAutoSelect: true,
          fetch: (text, update) => {
            if (text === '') {
              update(this.selectAutocompleteValues)
              return
            }
            // eslint-disable-next-line no-param-reassign
            text = text.toLowerCase()
            const suggestions = this.selectAutocompleteValues.filter((n) => n.label.toLowerCase().indexOf(text) !== -1)
            update(suggestions)
          },
          onSelect: (item) => {
            this.changeValue(item.value)
          },
        })
      }
    })
  },
}
